import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/api';
import './AdminPage.css';

// Report filters use UTC-3 wall-clock time regardless of the device timezone
const UTC_OFFSET_HOURS = -3;
const toUtcMinus3Input = (date) => new Date(date.getTime() + UTC_OFFSET_HOURS * 3600 * 1000).toISOString().slice(0, 16);

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [history, setHistory] = useState([]);
  const [report, setReport] = useState([]);
  const [activeTab, setActiveTab] = useState('articulos');
  const [from, setFrom] = useState(() => toUtcMinus3Input(new Date(Date.now() - 7 * 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => toUtcMinus3Input(new Date()));
  const [newUser, setNewUser] = useState({ username: '', displayName: '', password: '', role: 'user' });
  const [newArticle, setNewArticle] = useState({ id: '', name: '', description: '', category: 'bebidas', price: '' });
  const [editingArticle, setEditingArticle] = useState(null);
  const [error, setError] = useState('');

  const formatAmount = (value) => Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const loadUsers = async () => {
    const data = await apiRequest('users');
    setUsers(data.users);
  };

  const loadArticles = async () => {
    const data = await apiRequest('articles');
    const allArticles = [];
    Object.keys(data.articles).forEach(category => {
      data.articles[category].forEach(article => {
        allArticles.push({ ...article, category });
      });
    });
    setArticles(allArticles);
  };

  const loadHistory = async () => {
    const data = await apiRequest('price-history');
    setHistory(data.history);
  };

  const loadReport = async () => {
    const query = new URLSearchParams({ from, to });
    const data = await apiRequest(`reports/tickets-by-user?${query.toString()}`);
    setReport(data.report);
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    Promise.all([loadUsers(), loadArticles(), loadHistory(), loadReport()]).catch(err => setError(err.message));
  }, [isAdmin, navigate]);

  const createUser = async (event) => {
    event.preventDefault();
    setError('');

    try {
      await apiRequest('users', {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      setNewUser({ username: '', displayName: '', password: '', role: 'user' });
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleUserActive = async (user) => {
    setError('');

    try {
      await apiRequest(`users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          displayName: user.display_name,
          role: user.role,
          active: !user.active
        })
      });
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const createArticle = async (event) => {
    event.preventDefault();
    setError('');

    try {
      await apiRequest('articles', {
        method: 'POST',
        body: JSON.stringify({
          id: newArticle.id,
          name: newArticle.name,
          description: newArticle.description,
          category: newArticle.category,
          price: Number(newArticle.price)
        })
      });
      setNewArticle({ id: '', name: '', description: '', category: 'bebidas', price: '' });
      await loadArticles();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateArticle = async (article) => {
    setError('');

    try {
      await apiRequest(`articles/${article.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: article.name,
          description: article.description,
          category: article.category,
          price: Number(article.price),
          active: article.active
        })
      });
      setEditingArticle(null);
      await loadArticles();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleArticleActive = async (article) => {
    setError('');

    try {
      await apiRequest(`articles/${article.id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: !article.active })
      });
      await loadArticles();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('¿Está seguro de eliminar este artículo?')) {
      return;
    }

    setError('');

    try {
      await apiRequest(`articles/${articleId}`, {
        method: 'DELETE'
      });
      await loadArticles();
    } catch (err) {
      setError(err.message);
    }
  };

  const reportTotals = report.reduce((acc, row) => ({
    tickets: acc.tickets + Number(row.ticket_count || 0),
    price: acc.price + Number(row.total_price || 0)
  }), { tickets: 0, price: 0 });

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button onClick={() => navigate('/')}>← Volver</button>
        <h1>Administración</h1>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'articulos' ? 'active' : ''}`}
          onClick={() => setActiveTab('articulos')}
        >
          Artículos
        </button>
        <button 
          className={`admin-tab ${activeTab === 'usuarios' ? 'active' : ''}`}
          onClick={() => setActiveTab('usuarios')}
        >
          Usuarios
        </button>
        <button 
          className={`admin-tab ${activeTab === 'reporte' ? 'active' : ''}`}
          onClick={() => setActiveTab('reporte')}
        >
          Reporte de Tickets
        </button>
        <button 
          className={`admin-tab ${activeTab === 'historial' ? 'active' : ''}`}
          onClick={() => setActiveTab('historial')}
        >
          Historial de Precios
        </button>
      </div>

      {activeTab === 'articulos' && (
        <section className="admin-section">
        <h2>Artículos</h2>
        <form className="user-form" onSubmit={createArticle}>
          <input placeholder="ID (único)" value={newArticle.id} onChange={(event) => setNewArticle(prev => ({ ...prev, id: event.target.value }))} />
          <input placeholder="Nombre" value={newArticle.name} onChange={(event) => setNewArticle(prev => ({ ...prev, name: event.target.value }))} />
          <input placeholder="Descripción" value={newArticle.description} onChange={(event) => setNewArticle(prev => ({ ...prev, description: event.target.value }))} />
          <select value={newArticle.category} onChange={(event) => setNewArticle(prev => ({ ...prev, category: event.target.value }))}>
            <option value="bebidas">Bebidas</option>
            <option value="comidas">Comidas</option>
          </select>
          <input placeholder="Precio" type="number" step="0.01" value={newArticle.price} onChange={(event) => setNewArticle(prev => ({ ...prev, price: event.target.value }))} />
          <button type="submit">Crear</button>
        </form>
        <div className="admin-table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Categoría</th>
                <th>Precio</th>
                <th>Estado</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {articles.map(article => (
                <tr key={article.id}>
                  {editingArticle?.id === article.id ? (
                    <>
                      <td><input value={editingArticle.id} onChange={(e) => setEditingArticle(prev => ({ ...prev, id: e.target.value }))} /></td>
                      <td><input value={editingArticle.name} onChange={(e) => setEditingArticle(prev => ({ ...prev, name: e.target.value }))} /></td>
                      <td><input value={editingArticle.description || ''} onChange={(e) => setEditingArticle(prev => ({ ...prev, description: e.target.value }))} /></td>
                      <td>
                        <select value={editingArticle.category} onChange={(e) => setEditingArticle(prev => ({ ...prev, category: e.target.value }))}>
                          <option value="bebidas">Bebidas</option>
                          <option value="comidas">Comidas</option>
                        </select>
                      </td>
                      <td><input type="number" step="0.01" value={editingArticle.price} onChange={(e) => setEditingArticle(prev => ({ ...prev, price: e.target.value }))} /></td>
                      <td>
                        <select value={editingArticle.active} onChange={(e) => setEditingArticle(prev => ({ ...prev, active: e.target.value === 'true' }))}>
                          <option value="true">Activo</option>
                          <option value="false">Inactivo</option>
                        </select>
                      </td>
                      <td><button onClick={() => updateArticle(editingArticle)}>Guardar</button></td>
                      <td><button onClick={() => setEditingArticle(null)}>Cancelar</button></td>
                    </>
                  ) : (
                    <>
                      <td>{article.id}</td>
                      <td>{article.name}</td>
                      <td>{article.description || '-'}</td>
                      <td>{article.category}</td>
                      <td>${Number(article.price || 0).toFixed(2)}</td>
                      <td>{article.active ? 'Activo' : 'Inactivo'}</td>
                      <td><button onClick={() => toggleArticleActive(article)}>{article.active ? 'Desactivar' : 'Activar'}</button></td>
                      <td><button onClick={() => setEditingArticle(article)}>Editar</button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'usuarios' && (
      <section className="admin-section">
        <h2>Usuarios</h2>
        <form className="user-form" onSubmit={createUser}>
          <input placeholder="Usuario" value={newUser.username} onChange={(event) => setNewUser(prev => ({ ...prev, username: event.target.value }))} />
          <input placeholder="Nombre" value={newUser.displayName} onChange={(event) => setNewUser(prev => ({ ...prev, displayName: event.target.value }))} />
          <input placeholder="Contraseña" type="password" value={newUser.password} onChange={(event) => setNewUser(prev => ({ ...prev, password: event.target.value }))} />
          <select value={newUser.role} onChange={(event) => setNewUser(prev => ({ ...prev, role: event.target.value }))}>
            <option value="user">Usuario</option>
            <option value="admin">Administrador</option>
          </select>
          <button type="submit">Crear</button>
        </form>
        <div className="admin-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.display_name}</td>
                  <td>{user.role}</td>
                  <td>{user.active ? 'Activo' : 'Inactivo'}</td>
                  <td><button onClick={() => toggleUserActive(user)}>{user.active ? 'Desactivar' : 'Activar'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'reporte' && (
      <section className="admin-section">
        <h2>Reporte de tickets por usuario</h2>
        <div className="report-filters">
          <label>Desde <input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
          <label>Hasta <input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} /></label>
          <button onClick={() => { setError(''); loadReport().catch(err => setError(err.message)); }}>Actualizar</button>
        </div>
        <div className="report-total">Total tickets: {reportTotals.tickets} | Total: ${formatAmount(reportTotals.price)}</div>
        <div className="admin-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Artículo</th>
                <th>Cantidad</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {report.map(row => (
                <tr key={`${row.user_id}-${row.article_id}`}>
                  <td>{row.display_name}</td>
                  <td>{row.article_name}</td>
                  <td>{row.ticket_count}</td>
                  <td>${formatAmount(row.total_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'historial' && (
      <section className="admin-section">
        <h2>Historial de precios</h2>
        <div className="admin-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Artículo</th>
                <th>Anterior</th>
                <th>Nuevo</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              {history.map(item => (
                <tr key={item.id}>
                  <td>{new Date(item.changed_at).toLocaleString()}</td>
                  <td>{item.article_name}</td>
                  <td>${Number(item.old_price).toFixed(2)}</td>
                  <td>${Number(item.new_price).toFixed(2)}</td>
                  <td>{item.display_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
};

export default AdminPage;
